import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Auto-rotating "Featured Projects" carousel.
 *
 * Pulls hero case-study photos from the `services` array (passed in as a prop)
 * and rotates through a curated selection. Click-through links straight to
 * the parent service page.
 *
 * No external library — built on a CSS transform + setInterval timer.
 */

// Slug-ordered priority — we want our strongest visuals first.
const FEATURED_SLUGS = [
  "terraced-housing",   // Castor Bay 2-dwelling render
  "coastal",            // Boatshed
  "residential",        // Cliff-top residence
  { slug: "commercial", caseStudyTitle: "Burger Burger — Torbay Restaurant & Hospitality Consent" },
  "childcare-healthcare",
  "other-services",     // Architects portfolio
];

export default function FeaturedProjectsCarousel({ services = [] }) {
  const slides = buildSlides(services);
  const SLIDE_MS = 5500;
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const total = slides.length;
  const pausedRef = useRef(false);

  // Single rAF loop drives both the slide change and the progress bar fill.
  useEffect(() => {
    if (total <= 1) return;
    let raf;
    let start = performance.now();
    const tick = (now) => {
      if (!pausedRef.current) {
        const elapsed = now - start;
        const pct = Math.min(elapsed / SLIDE_MS, 1);
        setProgress(pct);
        if (pct >= 1) {
          start = now;
          setActive((i) => (i + 1) % total);
        }
      } else {
        // Pause: keep the bar where it is and reset the timer reference so
        // unpausing resumes from the current fill, not from zero.
        start = now - progress * SLIDE_MS;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // When the user jumps to a slide via the dots, reset the bar.
  const jumpTo = (i) => {
    setActive(i);
    setProgress(0);
  };

  if (total === 0) return null;

  return (
    <section
      className="relative bg-ink border-t border-neutral-900 overflow-hidden"
      data-testid="featured-projects-carousel"
      onMouseEnter={() => (pausedRef.current = true)}
      onMouseLeave={() => (pausedRef.current = false)}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-20">
        <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
          Featured Projects
        </p>
        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-10">
          Recent Work<span className="italic text-gray-400">, consented.</span>
        </h2>
      </div>

      {/* Slide track */}
      <div className="relative h-[60vh] md:h-[70vh] min-h-[420px] max-h-[720px]">
        {slides.map((slide, i) => (
          <Link
            key={slide.key}
            to={slide.href}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === active ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            data-testid={`featured-slide-${i}`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              loading={i === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-10 md:pb-14 max-w-7xl mx-auto">
              <p className="text-xs md:text-sm uppercase tracking-widest text-gray-300 mb-3">
                {slide.serviceTitle}
                {slide.location ? <span> · {slide.location}</span> : null}
              </p>
              <h3 className="font-heading text-2xl md:text-4xl lg:text-5xl text-white font-semibold leading-tight max-w-3xl">
                {slide.title}
              </h3>
              <span className="inline-flex items-center gap-2 mt-6 text-white uppercase tracking-widest text-xs md:text-sm hover:gap-4 transition-all duration-200">
                View Case Study
                <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        ))}
        {/* Progress bar — count-down to the next auto-advance */}
        <div className="absolute left-0 right-0 bottom-0 h-[3px] bg-white/10 z-20" aria-hidden="true">
          <div
            className="h-full bg-white transition-[width] duration-100 ease-linear"
            style={{ width: `${Math.round(progress * 100)}%` }}
            data-testid="carousel-progress"
          />
        </div>
      </div>

      {/* Dots */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 md:py-8 flex items-center justify-between">
        <div className="flex gap-3" data-testid="carousel-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => jumpTo(i)}
              aria-label={`Show slide ${i + 1}`}
              className={`h-[2px] transition-all duration-300 ${
                i === active ? "bg-white w-10" : "bg-neutral-600 w-6 hover:bg-neutral-400"
              }`}
              data-testid={`carousel-dot-${i}`}
            />
          ))}
        </div>
        <p className="hidden md:block text-xs uppercase tracking-widest text-gray-500">
          {String(active + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </p>
      </div>
    </section>
  );
}

function buildSlides(services) {
  const bySlug = Object.fromEntries(services.map((s) => [s.slug, s]));
  const out = [];
  for (const entry of FEATURED_SLUGS) {
    const slug = typeof entry === "string" ? entry : entry.slug;
    const pinnedTitle = typeof entry === "string" ? null : entry.caseStudyTitle;
    const s = bySlug[slug];
    if (!s) continue;
    const list = s.case_studies || [];
    const cs = pinnedTitle ? list.find((c) => c.title === pinnedTitle) : list[0];
    if (!cs || !cs.image_url) continue;
    out.push({
      key: `${slug}-${cs.id || cs.title}`,
      image: cs.image_url,
      title: cs.title,
      location: cs.location,
      serviceTitle: s.title,
      href: `/services/${s.slug}`,
    });
  }
  return out;
}
